import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  PencilLine,
  Printer,
  RefreshCcw,
  ScrollText,
  Search,
  Trash,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";

import { Button } from "../utils/components/ui/button";
import {
  useDeleteQuotation,
  useGetAllQuotation,
  useQuotePrint,
} from "../service/quatationService";
import { useQueryManager } from "../hooks/useQueryManager";
import { DateRangePicker } from "../components/DateRangePicker";
import QuotationSaveModal from "../components/forms/QuotationSaveModal";
import Loading from "../components/common/LoaderCommon";
import { cn } from "../utils/cn";
import DraftToQuotationModal from "../components/forms/DraftToQuotationModal";

type Props = {};

const Quotation = (props: Props) => {
  const { getValues, setValue, allParams, clearAll } = useQueryManager();
  const [debouncedSearchKey] = useDebounce(allParams.searchKey, 500);
  const [debouncedDateRange] = useDebounce(allParams.dateRange, 800);
  const [conversionModal, setConversionModal] = useState(false);
  const [draftId, setDraftId] = useState<string>("");
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  let fromDate = "";
  let toDate = "";
  if (debouncedDateRange) {
    try {
      const parsed = JSON.parse(debouncedDateRange);
      fromDate = parsed.from || "";
      toDate = parsed.to || "";
    } catch (e) {
      fromDate = "";
      toDate = "";
    }
  }

  const params = Object.fromEntries(
    Object.entries({
      pageNo: page,
      pageSize: 10,
      quotationNumber: debouncedSearchKey || "",
      customerName: "",
      fromDate,
      toDate,
      paidAmountFrom: Number(getValues("paidAmountFrom")[0]) || null,
      paidAmountTo: Number(getValues("paidAmountTo")[0]) || null,
      quotationAmountFrom: Number(getValues("quotationAmountFrom")[0]) || null,
      quotationAmountTo: Number(getValues("quotationAmountTo")[0]) || null,
      salesStatus: getValues("salesStatus")[0] || "",
      isDraft: false,
    }).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

  const { data: quotationData, isLoading } = useGetAllQuotation(params);
  const deleteQuotation = useDeleteQuotation({
    onSuccess: (data: any) => {
      toast.success(data);
      queryClient.invalidateQueries({ queryKey: ["useGetAllQuotation"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete quotation: ${error.message}`);
    },
  });

  const PrintQuote = useQuotePrint({
    onSuccess: (data: string) => {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(data);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      } else {
        toast.error("Popup blocked! Please allow popups for this site.");
      }
      queryClient.invalidateQueries({ queryKey: ["useGetAllInvoice"] });
    },
    onError: (error: Error) => {
      toast.error(`Error printing invoice: ${error.message}`);
    },
  });

  const totalQuotations = quotationData?.length || 0;
  const invoicedCount = useMemo(
    () =>
      quotationData?.filter((item: any) => item.status === "INVOICE").length ||
      0,
    [quotationData],
  );

  const statusStyles = (status?: string) => {
    if (status === "INVOICE") return "bg-[#EAF2FF] text-[#1A5EF9]";
    if (status === "QUOTATION") return "bg-[#EEF8E8] text-[#5A8F17]";
    if (status === "QUOTATION_REJECTED") return "bg-[#FDEEEE] text-[#D14343]";
    return "bg-[#FFF4E8] text-[#E08A1E]";
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button
            onClick={() => navigate("/create-quotation")}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
          >
            Create New Quotation
          </Button>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid flex-1] gap-4 xl:grid-cols-[minmax(220px,1.15fr)_minmax(220px,0.85fr)_minmax(280px,1fr)_minmax(280px,1fr)]">
              <div className="flex flex-col gap-2 max-w-[230px]">
                <p className="select-none text-sm font-medium text-transparent">
                  Search
                </p>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                  <input
                    autoFocus={
                      !!(allParams.searchKey && allParams.searchKey.length > 0)
                    }
                    value={allParams?.searchKey || ""}
                    type="text"
                    placeholder="Search by quote number"
                    className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                    onChange={(e) => setValue("searchKey", e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 justify-center items-center">
                <p className="select-none text-sm font-medium text-transparent">
                  Quote Date
                </p>
                <DateRangePicker
                  className="h-12 p-0 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] flex items-center "
                  onChange={(range) => {
                    setValue("dateRange", range ? JSON.stringify(range) : "");
                  }}
                  value={
                    allParams.dateRange
                      ? JSON.parse(allParams.dateRange)
                      : undefined
                  }
                />
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-[#50627E]">
                  Quoted Amount
                </p>
                <div className="flex h-12 items-center gap-3 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-3">
                  <input
                    onChange={(e) =>
                      setValue("quotationAmountFrom", e.target.value)
                    }
                    value={
                      allParams.quotationAmountFrom
                        ? allParams.quotationAmountFrom
                        : ""
                    }
                    min={0}
                    className="h-full w-full bg-transparent text-sm outline-none"
                    placeholder="From"
                    type="number"
                  />
                  <span className="text-[#8D98A7]">-</span>
                  <input
                    onChange={(e) =>
                      setValue("quotationAmountTo", e.target.value)
                    }
                    value={
                      allParams.quotationAmountTo
                        ? allParams.quotationAmountTo
                        : ""
                    }
                    min={0}
                    placeholder="To"
                    className="h-full w-full bg-transparent text-sm outline-none"
                    type="number"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-[#50627E]">
                  Actual Amount
                </p>
                <div className="flex h-12 items-center gap-3 rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-3">
                  <input
                    min={0}
                    onChange={(e) => setValue("paidAmountFrom", e.target.value)}
                    value={
                      allParams.paidAmountFrom ? allParams.paidAmountFrom : ""
                    }
                    className="h-full w-full bg-transparent text-sm outline-none"
                    placeholder="From"
                    type="number"
                  />
                  <span className="text-[#8D98A7]">-</span>
                  <input
                    onChange={(e) => setValue("paidAmountTo", e.target.value)}
                    value={allParams.paidAmountTo ? allParams.paidAmountTo : ""}
                    min={0}
                    placeholder="To"
                    className="h-full w-full bg-transparent text-sm outline-none"
                    type="number"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <p className="select-none text-sm font-medium text-transparent">
                Actions
              </p>
              <Button
                onClick={() => clearAll()}
                className="h-12 rounded-[14px] border border-[#BFD1FF] bg-white px-5 text-[#1A5EF9] hover:bg-[#EEF4FF]"
              >
                <RefreshCcw className="mr-2 size-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#21314A]">
                Quotation Directory
              </h3>
              <p className="mt-1 text-sm text-[#7D8998]">
                Review, print, edit, or remove quotation records from one
                listing.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-[12px] bg-[#F5F8FD] px-4 py-2 text-sm text-[#7D8998]">
                <span className="font-semibold text-[#253650]">
                  {totalQuotations}
                </span>{" "}
                quotations found
              </span>
              <div className="flex items-center gap-2">
                <button
                  className={cn(
                    "rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD]",
                    { "cursor-not-allowed opacity-50": page === 0 },
                  )}
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <div className="rounded-[10px] bg-[#1A5EF9] px-3 py-2 text-sm font-semibold text-white">
                  {page + 1}
                </div>
                <button
                  className={cn(
                    "rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD]",
                    {
                      "cursor-not-allowed opacity-50":
                        quotationData?.length === 0,
                    },
                  )}
                  disabled={quotationData?.length === 0}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                  <th className="px-6 py-4">Quotation Number</th>
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4 text-right">Quoted Amount</th>
                  <th className="px-6 py-4 text-right">Actual Amount</th>
                  <th className="px-6 py-4">Quotation Date</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {quotationData?.length ? (
                  quotationData.map((item: any) => (
                    <tr
                      key={item.id}
                      className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                    >
                      <td className="px-6 py-4 font-semibold text-[#24344F]">
                        {item.quotationNumber || "-"}
                      </td>
                      <td className="px-6 py-4">
                        {item.customer?.username || "-"}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                        {Number(item.totalAmount || 0).toFixed(3)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                        {Number(item.totalAmount || 0).toFixed(3)}
                      </td>
                      <td className="px-6 py-4">{item.quotationDate || "-"}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyles(item.status)}`}
                        >
                          {item.status || "PENDING"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() =>
                              navigate(`/create-quotation?id=${item.id}`)
                            }
                            title="Edit quotation"
                            aria-label="Edit quotation"
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                          >
                            <PencilLine className="size-4" />
                          </button>
                          <button
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            title="Convert to invoice"
                            aria-label="Convert to invoice"
                            onClick={() => {
                              setModalOpen(true);
                              setQuoteData(item);
                            }}
                          >
                            <ScrollText className="size-4" />
                          </button>
                          <button
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            title="Print quotation"
                            aria-label="Print quotation"
                            onClick={() =>
                              PrintQuote.mutate({ invoiceId: String(item.id) })
                            }
                          >
                            <Printer className="size-4" />
                          </button>
                          <button
                            onClick={() =>
                              deleteQuotation.mutate(String(item.id))
                            }
                            title="Delete quotation"
                            aria-label="Delete quotation"
                            className="rounded-[10px] border border-[#F5D4D4] p-2 text-[#B95A5A] transition hover:border-[#EAA7A7] hover:text-[#D14343]"
                          >
                            <Trash className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                    >
                      No quotations match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <QuotationSaveModal
        quoteData={quoteData}
        setQuoteData={setQuoteData}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onContinue={() => {
          setModalOpen(false);
        }}
        quotationAmount={quotationData?.[0]?.totalAmount?.toString() || "0"}
        onClose={() => setModalOpen(false)}
      />
      <DraftToQuotationModal
        onCancel={() => {
          setConversionModal(false);
          setDraftId("");
        }}
        id={draftId}
        open={conversionModal}
        onClose={() => {
          setConversionModal(false);
          setDraftId("");
        }}
        setId={setDraftId}
      />
    </>
  );
};

export default Quotation;
